import { group } from 'console';

import styled from 'styled-components';

import BasicRadialBarChart from 'components/common/chart/BasicRadialBarChart';
import SymmetricVerticalBarChart, {
	SymmetricVerticalBarChartDataItem,
} from 'components/common/chart/SymmetricVerticalBarChart';
import { useGetGroupInsights } from 'hooks/queries/dashboard';
import useGetSearchParams from 'hooks/useGetSearchParams';
import CoreLoading from 'shared/CoreLoading';
import { ReportSectionCard, ReportTopHeader } from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

function AudienceStats() {
	const [groupId] = useGetSearchParams(['groupId']);

	const mockAudienceDemographics = {
		audienceDemographics: {
			men: {
				'18-24': 0,
				'25-34': 0,
				'35-44': 0,
				'45-54': 0,
				'55+': 0,
			},
			women: {
				'18-24': 0,
				'25-34': 0,
				'35-44': 0,
				'45-54': 0,
				'55+': 0,
			},
		},
	};

	const { data: groupInsights, isLoading: groupInsightsLoading } =
		useGetGroupInsights(groupId);

	const transformAgeData = (data: Record<string, number>) => {
		return Object.fromEntries(
			Object.entries(data)
				.map(([age, value]) => ({
					age,
					ageNum: age === '55+' ? 55 : parseInt(age.split('-')[0]),
					value,
				}))
				.sort((a, b) => a.ageNum - b.ageNum)
				.map(({ age, value }) => [age, value]),
		);
	};

	const men = transformAgeData(
		groupInsights?.audienceDemographics.men ||
			mockAudienceDemographics.audienceDemographics.men,
	);

	const women = transformAgeData(
		groupInsights?.audienceDemographics.women ||
			mockAudienceDemographics.audienceDemographics.women,
	);

	// 최대값과 두 번째로 큰 값 구하기
	const allValues = [...Object.values(women), ...Object.values(men)];
	const sortedValues = [...new Set(allValues)].sort((a, b) => b - a);
	const maxValue = sortedValues[0]; // 최대값
	const secondMaxValue = sortedValues[1]; // 두 번째로 큰 값

	let maxGender = '';
	let maxKey = '';

	for (const [key, value] of Object.entries(women)) {
		if (value === maxValue) {
			maxGender = '여성';
			maxKey = key;
			break;
		}
	}

	if (!maxGender) {
		for (const [key, value] of Object.entries(men)) {
			if (value === maxValue) {
				maxGender = '남성';
				maxKey = key;
				break;
			}
		}
	}

	// const womenData: SymmetricVerticalBarChartDataItem[] = Object.values(women).map(
	// 	(value) => ({
	// 		valuePercentage: value / 100,
	// 		valueColor: '#FFFFFF',
	// 		value:
	// 			value === maxValue || value === secondMaxValue
	// 				? `${Math.floor(value)}%`
	// 				: undefined,
	// 		barColor:
	// 			value === maxValue ? '#FF5452' : value === secondMaxValue ? '#000000' : '#C8CED5',
	// 	}),
	// );

	// const menData: SymmetricVerticalBarChartDataItem[] = Object.values(men).map(
	// 	(value) => ({
	// 		valuePercentage: value / 100,
	// 		valueColor: '#FFFFFF',
	// 		value:
	// 			value === maxValue || value === secondMaxValue
	// 				? `${Math.floor(value)}%`
	// 				: undefined,
	// 		barColor:
	// 			value === maxValue ? '#FF5452' : value === secondMaxValue ? '#000000' : '#C8CED5',
	// 	}),
	// );
	// 더미 데이터 설정
	const dummyPercentages = [0.1, 0.1, 0.1, 0.1, 0.1]; // 상대적 비율로 설정된 더미 데이터

	const womenData: SymmetricVerticalBarChartDataItem[] = Object.values(women).map(
		(value, index) => ({
			valuePercentage:
				value === maxValue || value === secondMaxValue
					? value / maxValue // 최대값 대비 상대적 비율 계산
					: dummyPercentages[index], // 나머지는 더미 데이터 사용
			valueColor: '#FFFFFF',
			value:
				value === maxValue || value === secondMaxValue
					? `${Math.floor(value)}%` // top2는 실제 값 표시
					: undefined,
			barColor:
				value === maxValue ? '#FF5452' : value === secondMaxValue ? '#000000' : '#C8CED5',
		}),
	);

	const menData: SymmetricVerticalBarChartDataItem[] = Object.values(men).map(
		(value, index) => ({
			valuePercentage:
				value === maxValue || value === secondMaxValue
					? value / maxValue // 최대값 대비 상대적 비율 계산
					: dummyPercentages[index], // 나머지는 더미 데이터 사용
			valueColor: '#FFFFFF',
			value:
				value === maxValue || value === secondMaxValue
					? `${Math.floor(value)}%` // top2는 실제 값 표시
					: undefined,
			barColor:
				value === maxValue ? '#FF5452' : value === secondMaxValue ? '#000000' : '#C8CED5',
		}),
	);

	const ageCategories = Object.keys(men);

	if (groupInsightsLoading) {
		return (
			<ReportSectionCard _width="67%">
				<ReportTopHeader>오디언스 인구통계</ReportTopHeader>
				<Grid flex="center" _height="100%">
					<CoreLoading color_type="gray" />
				</Grid>
			</ReportSectionCard>
		);
	}

	return (
		<>
			<ReportSectionCard _width="67%">
				<ReportTopHeader>오디언스 인구통계</ReportTopHeader>
				<Text typo="p" _margin="0 0 16px 0">
					주요 오디언스는 {maxKey}세 {maxGender} ({Math.trunc(maxValue)}%)입니다.
				</Text>

				<Grid flex="start" align_items="center">
					{/* 그룹 바 차트 */}
					<SymmetricVerticalBarChart
						topDataLabel="여성"
						bottomDataLabel="남성"
						topData={womenData}
						bottomData={menData}
						categories={ageCategories}
						categoriesColor="#868E96"
						barWidth={58}
					/>

					<ReportSectionCard _height="284px">
						<Grid>
							<RelativeGrid>
								<AbsoluteChart>
									<BasicRadialBarChart
										width="200px"
										height="216px"
										value={Math.floor(maxValue)}
										value_name={`${maxKey}세 ${maxGender}`}
										label_value_family="SUIT"
										label_value_size="16px"
										label_value_weight="bold"
										color={'#FF5733'}
									/>
								</AbsoluteChart>
							</RelativeGrid>
						</Grid>
					</ReportSectionCard>
				</Grid>
			</ReportSectionCard>
		</>
	);
}

const { Grid, Text } = style;

const RelativeGrid = styled(Grid)`
	position: relative;
`;

const AbsoluteChart = styled.div`
	position: absolute;
`;

export default AudienceStats;
